import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';

import type { IrmDataProvider } from '../../providers/types';
import type { MyApiKey } from '../../types/types';
import { newKey } from '../admin/apiKeys';

export const MY_API_KEYS_QUERY_KEY = 'my-api-keys';

/**
 * The signed-in user's own keys, newest first.
 *
 * Read through the listMyApiKeys callable rather than Firestore: `api_keys` is
 * admin-only in firestore.rules, and the callable returns a fixed projection.
 * The list is therefore not live; the caller refetches after it creates or
 * revokes a key.
 *
 * The query is keyed by `uid`: a login without a logout in between (the auth
 * provider allows it) must not show the previous user's keys from the cache.
 */
export function useMyApiKeys(uid: string): {
    keys: MyApiKey[];
    loading: boolean;
    error: boolean;
    refetch: () => void;
} {
    const dataProvider = useDataProvider<IrmDataProvider>();
    const query = useQuery({
        queryKey: [MY_API_KEYS_QUERY_KEY, uid],
        queryFn: async () => (await dataProvider.listMyApiKeys()).keys,
    });
    return {
        keys: query.data ?? [],
        loading: query.isPending,
        error: query.isError,
        refetch: () => {
            void query.refetch();
        },
    };
}

/**
 * Mint the caller's own key.
 *
 * The raw key is generated and hashed HERE and never leaves the browser: the
 * callable receives only the SHA-256 and the log-safe prefix, so the secret
 * never crosses the network and cannot appear in a function log. Returns the
 * raw key, which is shown once and then unrecoverable.
 */
export async function createMyApiKey(
    dataProvider: IrmDataProvider,
    label: string
): Promise<string> {
    const { raw, key_hash, prefix } = await newKey();
    await dataProvider.createMyApiKey({ key_hash, prefix, label });
    return raw;
}

/** Revoke one of the caller's own keys. Takes up to 60 seconds to bite, the
 *  key cache TTL in api/partner/keys.py. */
export function revokeMyApiKey(
    dataProvider: IrmDataProvider,
    id: string
): Promise<{ ok: true; already_revoked: boolean }> {
    return dataProvider.revokeMyApiKey(id);
}
